// What the app renders for a URL, worked out the way the <Switch> in
// components/templates/Routing/Routing.js picks its route, so the Vercel middleware
// (/middleware.js) can answer with a real 404 where the app shows a not-found page.
// Imports keep their .js extension because the middleware loads this file outside webpack.
import APPLICATION_ROUTES from "../constants/application-routes.js";

export const PAGE_STATUS = {
  FOUND: "found",
  NOT_FOUND: "not-found",
  // A /:id video page or audio: whether it exists is up to the API
  VIDEO: "video",
};

// The <Switch>'s routes, in order: the first one that matches renders, and its path="*"
// (a redirect to the 404 page) catches the rest. pageStatus.test.js fails when this
// list and Routing.js drift apart.
export const ROUTES = [
  { path: "/YudShvat" },
  { path: "/YudShevat" },
  { path: APPLICATION_ROUTES.AUTO_SIGN_IN },
  { path: APPLICATION_ROUTES.CHECKOUT_PREFILLED },
  { path: APPLICATION_ROUTES.CHECKOUT },
  { path: APPLICATION_ROUTES.MY_ACCOUNT.ADDRESSES },
  { path: APPLICATION_ROUTES.MY_KIDS },
  { path: APPLICATION_ROUTES.DONATE, exact: true },
  { path: APPLICATION_ROUTES.PARTNERS_DONATION },
  { path: APPLICATION_ROUTES.MY_MAOR_APP_DONATION },
  { path: "/writing-competition/:id" },
  { path: "/contest" },
  { path: APPLICATION_ROUTES.WRITINNG_COMPETITION },
  { path: APPLICATION_ROUTES.WRITING_COMPETITON_VIDEO_FORM },
  { path: APPLICATION_ROUTES.WRITINNG_COMPETITION_SUCCESS },
  { path: APPLICATION_ROUTES.ARCHIVED_SUBMISSIONS },
  { path: APPLICATION_ROUTES.WRITINNG_COMPETITION_FORM },
  { path: APPLICATION_ROUTES.PARTNERS_ORDER_PREFILLED },
  { path: APPLICATION_ROUTES.PARTNERS_ORDER },
  { path: APPLICATION_ROUTES.PARTNERS_SUCCESS },
  { path: APPLICATION_ROUTES.DONATION_SUCCESS },
  { path: APPLICATION_ROUTES.MY_MAOR_SUCCESS },
  { path: APPLICATION_ROUTES.QUESTIONNAIRE_MAIN },
  { path: APPLICATION_ROUTES.QUESTIONNAIRE_SUCCESS },
  { path: APPLICATION_ROUTES.SIGNUP_SUCCESS },
  { path: APPLICATION_ROUTES.SPONSORS },
  { path: APPLICATION_ROUTES.COMING_SOON },
  { path: APPLICATION_ROUTES.DAILY_REBBE_VIDEO_MAIN },
  { path: APPLICATION_ROUTES.EMAIL_VERIFICATION_REQUEST },
  { path: APPLICATION_ROUTES.AUTH_PASSWORD_RECOVERY_REQUEST },
  { path: APPLICATION_ROUTES.EMAIL_VERIFY },
  { path: APPLICATION_ROUTES.EMAIL_VERIFICATION_SUCCESS },
  { path: APPLICATION_ROUTES.ILLUMINATE },
  { path: APPLICATION_ROUTES.LIBRARY },
  { path: APPLICATION_ROUTES.LIBRARY_PROECTED },
  { path: APPLICATION_ROUTES.MY_MAOR_CHECKOUT },
  { path: APPLICATION_ROUTES.MY_MAOR_ORDER },
  { path: APPLICATION_ROUTES.MY_MAOR_MAIN_OLD },
  { path: APPLICATION_ROUTES.MY_MAOR_PACKAGES },
  { path: APPLICATION_ROUTES.DONATION_REFUND_POLICY },
  { path: APPLICATION_ROUTES.NOT_FOUND },
  { path: APPLICATION_ROUTES.ACCESS_DENIED },
  { path: APPLICATION_ROUTES.PARTNERS_MAIN_HOME },
  { path: APPLICATION_ROUTES.HOME_PAGE },
  { path: APPLICATION_ROUTES.SALE },
  { path: APPLICATION_ROUTES.VERIFY_EMAIL },
  { path: APPLICATION_ROUTES.PARTNERS_PACKAGES },
  { path: APPLICATION_ROUTES.NINETEEN_KISLEV },
  { path: APPLICATION_ROUTES.BIRTHDAY_PACKAGES_PAGE },
  { path: APPLICATION_ROUTES.PASSWORD_RECOVERY_REQUEST },
  { path: APPLICATION_ROUTES.PASSWORD_RECOVERY_SUCCESS },
  { path: APPLICATION_ROUTES.PASSWORD_RECOVERY_UPDATE },
  { path: APPLICATION_ROUTES.PRIVACY_POLICY },
  { path: APPLICATION_ROUTES.LEGACY_GIFT, exact: true },
  { path: APPLICATION_ROUTES.PARTNER },
  { path: APPLICATION_ROUTES.PARTNERS },
  { path: APPLICATION_ROUTES.PLEDGE },
  { path: APPLICATION_ROUTES.SIGN_UP_SHORT },
  { path: APPLICATION_ROUTES.REFERRAL },
  { path: APPLICATION_ROUTES.COLORING_PAGES, exact: true },
  { path: APPLICATION_ROUTES.AUDIO_LIBRARY, exact: true },
  { path: APPLICATION_ROUTES.MY_MAOR_MAIN, exact: true },
  { path: APPLICATION_ROUTES.TEST },
  { path: APPLICATION_ROUTES.MY_MAOR_MAIN_ID, exact: true },
  { path: APPLICATION_ROUTES.PROFILE },
  { path: APPLICATION_ROUTES.SIGN_IN },
  { path: APPLICATION_ROUTES.SIGN_UP },
  { path: APPLICATION_ROUTES.AUTH_SIGN_IN },
  { path: APPLICATION_ROUTES.AUTH_SIGN_UP },
  { path: APPLICATION_ROUTES.MY_ACCOUNT.MAILING_LIST },
  { path: APPLICATION_ROUTES.MY_ACCOUNT.PAYMENT_METHOD_ALL },
  { path: APPLICATION_ROUTES.MY_ACCOUNT.PAYMENT_METHOD },
  { path: APPLICATION_ROUTES.SUBSCRIPTION_CANCELED },
  { path: APPLICATION_ROUTES.HOME, exact: true },
];

// The /:id route renders a page by the id's prefix (see its render prop in Routing.js)
export const VIDEO_ID_PREFIXES = ["k", "h", "i", "d"]; // MyMaor, IlluminateMain, DailyRebbeVideoMain
export const PAGE_ID_PREFIXES = ["c"]; // WritingCompetition, which renders whether the id exists or not

// A letter and a number: "k971", "k611.1", "c12"
const PREFIXED_ID = /^([a-z])\d+(\.\d+)?$/i;

const prefixOf = (id, prefixes) => {
  const match = PREFIXED_ID.exec(id);
  const prefix = match ? match[1].toLowerCase() : "";
  return prefixes.includes(prefix) ? prefix : "";
};

// The prefix of an id that is one of those letters and a number, or a video's share code (the
// id base64-encoded). "" for any other id: an audio's media slug, which can be any text
// ("sa11", "gn12"), or its share code. The audio library plays those, and the API says if
// they exist.
export const getIdPagePrefix = (id) => {
  let decodedId = "";
  try {
    decodedId = atob(id);
  } catch (err) {
    // not base64
  }

  return prefixOf(id, [...VIDEO_ID_PREFIXES, ...PAGE_ID_PREFIXES]) || prefixOf(decodedId, VIDEO_ID_PREFIXES);
};

const getIdPageStatus = (id) => {
  if (PAGE_ID_PREFIXES.includes(getIdPagePrefix(id))) return { status: PAGE_STATUS.FOUND };

  // A video page, or the audio library for a media slug. GET /video/exists takes the id as
  // it is in the URL
  return { status: PAGE_STATUS.VIDEO, videoId: id };
};

// Static segments and :params, the only route syntax the <Switch> uses
const ROUTE_PATH = /^\/$|^(\/([\w-]+|:\w+))+$/;

// Compiles a route the way react-router 5's matchPath does: case-insensitive, an optional
// trailing slash, and a prefix match (at a "/") unless the route is exact.
export const compileRoute = ({ path, exact }) => {
  if (!ROUTE_PATH.test(path)) throw new Error(`Unsupported route path: ${path}`);

  const source = path
    .split("/")
    .filter(Boolean)
    .map((segment) => (segment.startsWith(":") ? "\\/([^\\/]+?)" : `\\/${segment}`))
    .join("");
  return new RegExp(`^${source}(?:\\/(?=$))?${exact ? "$" : "(?=\\/|$)"}`, "i");
};

let compiledRoutes;

// pathname as in a request URL (still percent-encoded)
export const getPageStatus = (pathname) => {
  let path;
  try {
    // history decodes the path with decodeURI before react-router matches it
    path = decodeURI(pathname);
  } catch (err) {
    // history throws on these, so the app can't render at all
    return { status: PAGE_STATUS.NOT_FOUND };
  }

  if (!compiledRoutes) {
    compiledRoutes = ROUTES.map((route) => ({ ...route, regexp: compileRoute(route) }));
  }

  const route = compiledRoutes.find(({ regexp }) => regexp.test(path));
  if (!route || route.path === APPLICATION_ROUTES.NOT_FOUND) {
    return { status: PAGE_STATUS.NOT_FOUND };
  }
  if (route.path === APPLICATION_ROUTES.MY_MAOR_MAIN_ID) {
    return getIdPageStatus(route.regexp.exec(path)[1]);
  }
  return { status: PAGE_STATUS.FOUND };
};
